"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";

import "swiper/css";
import "swiper/css/navigation";

const noticias = [
  {
    img: "Midia-1.png",
    title: '"Como o Agibank usou o Agentforce da Salesforce para transformar o seu atendimento"',
    link: "https://braziljournal.com/brands/como-o-agibank-usou-o-agentforce-da-salesforce-para-transformar-o-seu-atendimento/",
  },
  {
    img: "Midia-2.png",
    title: '"Agibank lança software de IA para prevenção à lavagem de dinheiro e combate a fraudes"',
    link: "https://epocanegocios.globo.com/inteligencia-artificial/noticia/2025/02/agibank-lanca-software-de-ia-para-prevencao-a-lavagem-de-dinheiro-e-combate-a-fraudes.ghtml",
  },
  {
    img: "Midia-3.png",
    title: '"Agibank projeta alcançar 10% do mercado de crédito consignado até 2030"',
    link: "https://www.cnnbrasil.com.br/economia/negocios/agibank-projeta-alcancar-10-do-mercado-de-credito-consignado-ate-2030/",
  },
];

const ArrowLeft = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
  </svg>
);

const ArrowRight = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
  </svg>
);

export default function Midia() {
  const [swiperDesktop, setSwiperDesktop] = useState<SwiperType | null>(null);
  const [swiperMobile, setSwiperMobile] = useState<SwiperType | null>(null);

  return (
    <section className="bg-white py-20 lg:py-32 relative overflow-hidden z-10 w-full">
      <div className="absolute inset-0 z-0 pointer-events-none opacity-40">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src="/images/formacao-devs-ia/Pixels.png" alt="" className="w-full h-full object-cover" />
      </div>

      <div className="container-custom relative z-10">
        {/* Desktop */}
        <div className="hidden lg:grid grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="col-span-4 flex flex-col items-start relative z-20 py-8"
          >
            <h2 className="text-[56px] font-bold text-blue-main leading-tight mb-8 tracking-tight">
              Agibank na mídia
            </h2>
            <p className="text-xl text-blue-dark font-light mb-10 leading-relaxed">
              Aqui, a inteligência artificial vem pra somar,{" "}
              <span className="font-bold">desde que exista uma pessoa por trás que saiba operar.</span>
            </p>
            <div className="flex gap-4">
              <button
                onClick={() => swiperDesktop?.slidePrev()}
                className="w-12 h-12 rounded-full bg-secondary-main text-blue-dark flex items-center justify-center hover:bg-blue-main hover:text-white transition-all transform hover:scale-105 active:scale-95 shadow-md cursor-pointer"
              >
                <ArrowLeft />
              </button>
              <button
                onClick={() => swiperDesktop?.slideNext()}
                className="w-12 h-12 rounded-full bg-secondary-main text-blue-dark flex items-center justify-center hover:bg-blue-main hover:text-white transition-all transform hover:scale-105 active:scale-95 shadow-md cursor-pointer"
              >
                <ArrowRight />
              </button>
            </div>
          </motion.div>

          <div className="col-span-8 relative z-10">
            <div style={{ clipPath: "inset(-100vh -100vw -100vh -24px)" }} className="py-8 -my-8">
              <Swiper
                onSwiper={setSwiperDesktop}
                slidesPerView="auto"
                spaceBetween={32}
                modules={[Navigation]}
                className="!overflow-visible"
              >
                {noticias.map((noticia, index) => (
                  <SwiperSlide key={index} className="!w-[420px]">
                    <a
                      href={noticia.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block h-full group bg-white border border-gray-100 rounded-[32px] shadow-[0_10px_30px_rgba(0,15,68,0.12)] hover:shadow-[0_20px_40px_rgba(0,15,68,0.25)] hover:-translate-y-2 hover:scale-[1.02] transition-all duration-300 ease-out flex flex-col overflow-hidden"
                    >
                      <div className="p-4 pb-0 h-[250px]">
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                          src={`/images/formacao-devs-ia/${noticia.img}`}
                          alt="Notícia"
                          className="w-full h-full object-cover rounded-[24px] bg-gray-100"
                          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                            (e.target as HTMLImageElement).src = `https://placehold.co/400x300/0064f5/ffffff?text=Notícia+${index + 1}`;
                          }}
                        />
                      </div>
                      <div className="p-8 flex-1 flex items-center">
                        <p className="text-blue-main font-medium text-xl leading-snug group-hover:text-blue-medium transition-colors">
                          {noticia.title}
                        </p>
                      </div>
                    </a>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>
        </div>

        {/* Mobile */}
        <div className="flex flex-col lg:hidden w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="w-full flex flex-col items-start px-2"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-blue-main leading-[1.1] mb-6 tracking-tight">
              Agibank na mídia
            </h2>
            <p className="text-lg md:text-xl text-blue-dark font-light mb-8 leading-relaxed max-w-[90%] break-words">
              Aqui, a inteligência artificial vem pra somar,{" "}
              <span className="font-bold">desde que exista uma pessoa por trás que saiba operar.</span>
            </p>
            <div className="flex gap-4 mb-12">
              <button
                onClick={() => swiperMobile?.slidePrev()}
                className="w-12 h-12 rounded-full bg-secondary-main text-blue-dark flex items-center justify-center shadow-md active:scale-95 z-40 relative"
              >
                <ArrowLeft />
              </button>
              <button
                onClick={() => swiperMobile?.slideNext()}
                className="w-12 h-12 rounded-full bg-secondary-main text-blue-dark flex items-center justify-center shadow-md active:scale-95 z-40 relative"
              >
                <ArrowRight />
              </button>
            </div>
          </motion.div>

          <div className="w-full relative -mx-6 md:-mx-12">
            <Swiper
              onSwiper={setSwiperMobile}
              slidesPerView="auto"
              spaceBetween={24}
              slidesOffsetBefore={24}
              slidesOffsetAfter={24}
              modules={[Navigation]}
              className="!overflow-visible py-8"
            >
              {noticias.map((noticia, index) => (
                <SwiperSlide key={index} className="!w-[280px] md:!w-[340px]">
                  <a
                    href={noticia.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block h-full bg-white border border-gray-100 rounded-[32px] shadow-[0_10px_30px_rgba(0,15,68,0.15)] flex flex-col overflow-hidden"
                  >
                    <div className="p-4 pb-0 h-[200px]">
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src={`/images/formacao-devs-ia/${noticia.img}`}
                        alt="Notícia"
                        className="w-full h-full object-cover rounded-[24px] bg-gray-100"
                        onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                          (e.target as HTMLImageElement).src = `https://placehold.co/400x300/0064f5/ffffff?text=Notícia+${index + 1}`;
                        }}
                      />
                    </div>
                    <div className="p-6 flex-1 flex items-center">
                      <p className="text-blue-main font-medium text-base md:text-lg leading-snug">
                        {noticia.title}
                      </p>
                    </div>
                  </a>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      </div>
    </section>
  );
}
